import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from 'react-query'
import cn from 'classnames'
import Button from 'core/components/buttons/Button'
import Countdown from 'core/components/countdown/Countdown'
import SmartField from 'core/components/form/SmartField'
import Input from 'core/components/inputs/Input'
import Icon from 'core/components/icons/Icon'
import Modal from 'core/components/dialogs/Modal'
import { Settings, useSettings } from 'core/hooks/useSettings'
import APIService from 'api/APIService'
import useCurrentTeam from 'core/hooks/useCurrentTeam'

type Status = 'verifying_token' | 'code_pending' | 'success' | 'error'

interface FormValues {
  code: string
}

const SecondFactorAuthenticator = () => {
  const { set, get } = useSettings()
  const queryClient = useQueryClient()
  const mfa = useMemo(() => get('mfa') ?? {}, [get])
  const { action, onSuccess, continueButtonText } = mfa

  const { data: team } = useCurrentTeam()
  const team_token_id = team?.token_id

  const [phoneNumber, setPhoneNumber] = useState('')
  const [verificationToken, setVerificationToken] = useState('')
  const [tmpAccountIdCustomerToken, setTmpAccountIdCustomerToken] = useState<
    Pick<Settings['mfa'], 'accountId' | 'customerToken'>
  >({
    accountId: null,
    customerToken: null
  })
  const [status, setStatus] = useState<Status>('code_pending')

  const [canResend, setCanResend] = useState(false)
  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors }
  } = useForm<FormValues>({
    defaultValues: {
      code: ''
    }
  })

  const resetTmpValues = useCallback(() => {
    setTmpAccountIdCustomerToken({
      accountId: null,
      customerToken: null
    })
  }, [])

  const { mutate: sendCode, isLoading: isSendingCode } = useMutation(
    ['unit', 'second-factor-authenticator', 'send-code'],
    () =>
      APIService.unit.unitGetTeamCustomer({
        team_token_id: String(team_token_id),
        scope: 'read_write'
      }),
    {
      onSuccess(data) {
        setCanResend(false)
        resetTmpValues()
        if (data.verification_token) {
          setVerificationToken(data.verification_token)
          setPhoneNumber(data.phone)
          setStatus('code_pending')
        } else {
          set('mfa', {
            ...mfa,
            action: null,
            customerToken: data.customer_token,
            accountId: data.unit_account.unit_id
          })
          setPhoneNumber('')
          setVerificationToken('')
          setStatus('success')
          onSuccess?.()
        }
      },
      onError(error) {
        console.error(error)
      }
    }
  )

  const { mutate: verifyCode, isLoading: isVerifyingCode } = useMutation(
    ['unit', 'second-factor-authenticator', 'verify-code'],
    (code: string) =>
      APIService.unit.unitGetTeamCustomer({
        team_token_id: String(team_token_id),
        verification_token: verificationToken,
        verification_code: code,
        scope: 'read_write'
      }),
    {
      onSuccess(data) {
        if (data.customer_token) {
          setStatus('success')
          setTmpAccountIdCustomerToken({
            accountId: data.unit_account.unit_id,
            customerToken: data.customer_token
          })
          setVerificationToken('')
        } else if (data.verification_token === null) {
          setStatus('error')
        }
        setValue('code', '')
      },
      onError(error) {
        console.error(error)
        setStatus('error')
        resetTmpValues()
        setVerificationToken('')
      }
    }
  )

  const onErrorResendCode = useCallback(async () => {
    await sendCode()
    setValue('code', '')
    setStatus('code_pending')
  }, [sendCode, setValue])

  const onSubmit = useCallback(
    ({ code }: FormValues) => {
      verifyCode(code)
    },
    [verifyCode]
  )

  const onClickContinue = useCallback(() => {
    onSuccess?.()
    set('mfa', {
      ...mfa,
      action: null,
      ...tmpAccountIdCustomerToken
    })
  }, [mfa, onSuccess, set, tmpAccountIdCustomerToken])

  const onClose = useCallback(() => {
    setStatus('verifying_token')
    reset({
      code: ''
    })
    mfa.onClose?.()
    set('mfa', {
      ...mfa,
      action: null,
      onSuccess: null,
      onBack: null,
      onClose: null
    })
  }, [mfa, reset, set])

  const isValid = useMemo(() => Object.keys(errors).length === 0, [errors])

  const partialPhoneNumber = useMemo(
    () =>
      phoneNumber?.length
        ? '******' + phoneNumber.substring(phoneNumber.length - 4)
        : '******',
    [phoneNumber]
  )

  const modalIsOpen = useMemo(() => action === 'authenticate', [action])

  const content = useMemo(() => {
    if (status === 'verifying_token') {
      return (
        <div className="flex flex-col items-center">
          <Icon name="wallet" color="text-primary" size={40} className="mb-5" />
          <h2 className="subtitle-1 mb-4">Verifying your account</h2>
          <p className="text-dark-4">This may take a few moments</p>
        </div>
      )
    }
    if (status === 'code_pending') {
      return (
        <>
          <h2 className="subtitle-1 mb-4">Verify your phone number</h2>
          <p className="text-dark-4 mb-7">
            Enter the security code sent to {partialPhoneNumber}
          </p>
          <form onSubmit={handleSubmit(onSubmit)}>
            <SmartField
              controllerProps={{
                control,
                name: 'code',
                rules: {
                  validate: (value) => {
                    return (
                      value.length === 6 || 'Code must be 6 characters long'
                    )
                  }
                }
              }}
              label="6-digit code"
              orientation="vertical"
              className="mb-7"
            >
              <Input name="code" className="w-96" />
            </SmartField>
            <div className="flex flex-col gap-4">
              <Button
                type="submit"
                variant="primary"
                disabled={!isValid && isVerifyingCode}
                className={cn('flex justify-center items-center gap-1')}
              >
                Continue
                {isVerifyingCode && <Icon name="loading" size={10} />}
              </Button>
              <div className="flex flex-col items-center w-full">
                {!canResend && (
                  <div>
                    <span>You can request a new code in</span>{' '}
                    <Countdown
                      initialTime={60}
                      onTimeout={() => setCanResend(true)}
                    />{' '}
                    <span>seconds.</span>
                  </div>
                )}
                <Button
                  variant="link"
                  className={cn(
                    'flex justify-center items-center gap-1',
                    canResend
                      ? 'text-primary hover:text-primary-3'
                      : 'text-dark hover:text-dark-3'
                  )}
                  onClick={() => canResend && sendCode()}
                  disabled={!canResend}
                >
                  Resend code
                  {isSendingCode && <Icon name="loading" size={10} />}
                </Button>
              </div>
            </div>
          </form>
        </>
      )
    }
    if (status === 'success') {
      return (
        <div className="flex flex-col items-center">
          <Icon
            name="check"
            color="text-white"
            size={40}
            className="rounded-full bg-primary p-2 mb-5"
          />
          <h2 className="subtitle-1 mb-4">Success!</h2>
          <p className="text-dark mb-5">Your phone number has been verified.</p>
          <Button
            type="submit"
            variant="primary"
            onClick={onClickContinue}
            className="w-56"
          >
            {continueButtonText}
          </Button>
        </div>
      )
    }
    if (status === 'error') {
      return (
        <div className="flex flex-col items-center">
          <Icon
            name="exclamation-mark"
            color="text-danger"
            size={40}
            className="rounded-full bg-danger-1 p-2 mb-5"
          />
          <h2 className="subtitle-1 mb-4">
            Your identity could not be verified
          </h2>
          <p className="text-dark mb-5">Resend the code and try again.</p>
          <p className="text-dark mb-5">
            Need more assistance?{' '}
            <a
              href="https://www.everlance.com/contact-us"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Contact support
            </a>
            .
          </p>
          <Button
            type="submit"
            variant="primary"
            onClick={onErrorResendCode}
            className="w-40"
          >
            Resend code
          </Button>
        </div>
      )
    }
    return null
  }, [
    status,
    partialPhoneNumber,
    handleSubmit,
    onSubmit,
    control,
    isValid,
    isVerifyingCode,
    canResend,
    isSendingCode,
    sendCode,
    onClickContinue,
    continueButtonText,
    onErrorResendCode
  ])

  useEffect(() => {
    if (modalIsOpen) {
      setStatus('verifying_token')
      sendCode()
    }
  }, [modalIsOpen, sendCode])

  // @ts-ignore
  useEffect(() => {
    const onUnitPaymentCreated = () => {
      queryClient.invalidateQueries(['unit'])
    }
    window.addEventListener('unitPaymentCreated', onUnitPaymentCreated)
    return () =>
      window.removeEventListener('unitPaymentCreated', onUnitPaymentCreated)
  }, [queryClient])

  return (
    <Modal
      show={modalIsOpen}
      onClose={onClose}
      onBack={mfa.onBack ?? undefined}
    >
      <div className="flex flex-col items-center">{content}</div>
    </Modal>
  )
}

export default SecondFactorAuthenticator
